// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { FC } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled from 'styled-components';
import { routes } from '@/webapi/routes';
import {
  AccountContext,
  createAccountContext,
  isPrivatePath,
  useFetchUserContext,
} from '@/features/account-context';
import { breakpoints } from '@/components/responsive';
import { UnsupportedDevice } from '@/components/Unsupported';
import { AppEmbedModal } from '@/features/welcome-quiz/enable-app-embed/app-embed-modal';
import { Header } from '@/components/header';
import { MobileDashboardHeader } from '@/features/dashboard/shared/mobile-header';
import {
  PrivateHeaderControlsOutlet,
  PrivateHeaderProvider,
  usePrivateHeader,
} from '@/components/private-header';

export function Layout({
  children,
  location,
}: {
  children: any;
  location: { pathname: string };
}) {
  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          stage
          proxy
          analytics
        }
      }
    }
  `);
  const { stage, proxy, analytics } = data.site.siteMetadata;
  routes.withEnv(stage, proxy, analytics);
  const accountCtx = createAccountContext();
  useFetchUserContext(accountCtx, location?.pathname);
  const someRef = React.useRef(null);
  const isPrivate = isPrivatePath(location?.pathname);
  const hasAccount =
    accountCtx?.account?.isLoggedIn && accountCtx?.account?.store?.alias;

  return (
    <AccountContext.Provider value={accountCtx}>
      {accountCtx.isAppEmbedModalVisible && (
        <>
          <div ref={someRef} />
          <AppEmbedModal
            isVisible={accountCtx.isAppEmbedModalVisible}
            fromRef={someRef}
            setIsVisible={accountCtx.setIsAppEmbedModalVisible}
            header="Enable App Embed"
          />
        </>
      )}
      {isPrivate && hasAccount && (
        <PrivateLayout pathname={location.pathname}>
          <InnerLayout pathname={location.pathname}>{children}</InnerLayout>
        </PrivateLayout>
      )}
      {isPrivate && !hasAccount && (
        <InnerLayout pathname={location.pathname}>
          <PrivateRouteFallback />
        </InnerLayout>
      )}
      {!isPrivate && (
        <InnerLayout pathname={location.pathname}>{children}</InnerLayout>
      )}
    </AccountContext.Provider>
  );
}

const PrivateRouteFallback = styled.div`
  min-height: 100vh;
  width: 100%;
  background: #788675;
`;

function PrivateLayout({
  children,
  pathname,
}: {
  children: React.ReactNode;
  pathname: string;
}) {
  const hideHeader = pathname?.startsWith(`/editor`);

  if (hideHeader) {
    return (
      <PrivateHeaderProvider>
        <PrivateFullLayoutWrapper>
          <PrivateContent>{children}</PrivateContent>
        </PrivateFullLayoutWrapper>
      </PrivateHeaderProvider>
    );
  }

  return (
    <PrivateHeaderProvider>
      <PrivateLayoutWrapper>
        <PrivateHeader />
        <PrivateContent>{children}</PrivateContent>
      </PrivateLayoutWrapper>
    </PrivateHeaderProvider>
  );
}

function PrivateHeader() {
  const { hideBackToLive } = usePrivateHeader();

  return (
    <PrivateHeaderWrapper>
      <Header
        className="only-desktop"
        headerControls={<PrivateHeaderControlsOutlet />}
        hideBackToLive={hideBackToLive}
      />
      <MobileDashboardHeader />
    </PrivateHeaderWrapper>
  );
}

const PrivateLayoutWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: max-content minmax(0, 1fr);
  background: rgb(248, 249, 251);
`;

const PrivateFullLayoutWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: rgb(248, 249, 251);
`;

const PrivateHeaderWrapper = styled.div`
  width: 100%;

  .only-desktop {
    ${breakpoints.down(`md`)} {
      display: none !important;
    }
  }

  .only-mobile {
    ${breakpoints.up(`md`)} {
      display: none !important;
    }
  }
`;

const PrivateContent = styled.div`
  min-height: 0;
  height: 100%;
  width: 100%;
  overflow: auto;
`;

const InnerLayout: FC<{
  pathname: string;
  children: React.ReactNode;
}> = ({ children, pathname }) => {
  const isSupportedOnMobile =
    [
      ``,
      `/`,
      `/public/lp/2026_planning`,
      `/public/lp/2026_planning/`,
      `/public/fb/auth/cb`,
      `/public/fb/auth/cb/`,
      `/public/insta/auth/cb/`,
      `/public/insta/auth/cb`,
      `/public/privacy-policy`,
      `/public/privacy-policy/`,
      `/public/pricing/`,
      `/public/pricing`,
      `/public/comparison/dy`,
      `/public/comparison/dy/`,
      `/public/comparison/shoplift`,
      `/public/comparison/shoplift/`,
      `/public/comparison/rebuy`,
      `/public/comparison/rebuy/`,
      `/public/comparison/intelligems`,
      `/public/comparison/intelligems/`,
      `/public/whats-new/`,
      `/public/whats-new`,
      `/public/ig/`,
      `/public/ig`,
      `/public/talk-to-expert`,
      `/public/talk-to-expert/`,
      `/public/partners`,
      `/public/partners/`,
      `/pricing`,
      `/pricing/`,
      `/product-demo/`,
      `/product-demo`,
      `/public/affiliates/`,
      `/public/affiliates`,
      `/public/customers`,
      `/public/customers/`,
      `/public/pricing-v2/`,
      `/public/pricing-v2`,
      `/welcome`,
      `/welcome/`,
      `/await-email`,
      `/await-email/`,
      `/login`,
      `/login/`,
      `/dashboard`,
      `/dashboard/`,
      `/details`,
      `/details/`,
      `/accounts`,
      `/accounts/`,
      `/inspiration`,
      `/inspiration/`,
      `/404`,
      `/404/`,
    ].includes(pathname) ||
    pathname.includes(`/public/case-studies/`) ||
    pathname.includes(`/public/inspirations`);
  return isSupportedOnMobile ? (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{children}</>
  ) : (
    <>
      <HideOnMobile>{children}</HideOnMobile>
      <UnsupportedDevice pathname={pathname} />
    </>
  );
};

const HideOnMobile = styled.div`
  height: 100%;
  width: 100%;

  ${breakpoints.down(`md`)} {
    display: none;
  }
`;
